import { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Lock, Mail, ArrowLeft, Key } from 'lucide-react'
import api from '../services/api'

const ResetPassword = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [showPassword, setShowPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    const token = searchParams.get('token')
    const email = searchParams.get('email')

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues: {
            email: email || '',
            token: token || ''
        }
    })

    const onSubmit = async (data) => {
        setIsLoading(true)
        setError('')
        setSuccess('')

        try {
            const response = await api.post('/auth/reset-password', data)
            setSuccess(response.data.message)

            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate('/login')
            }, 2000)
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to reset password. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    if (!token || !email) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100">
                <div className="bg-white rounded-xl shadow-lg p-8 border border-indigo-100 max-w-md w-full mx-4">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Reset Link</h2>
                        <p className="text-gray-600 mb-6">This reset link is invalid or has expired.</p>
                        <Link
                            to="/forgot-password"
                            className="btn bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 to-indigo-800 text-white"
                        >
                            Request New Reset
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen flex">
            {/* Left side - Brand section */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-700 via-indigo-800 to-cyan-700 p-8">
                <div className="flex flex-col justify-center items-center text-center text-white w-full">
                    <img src="/assets/sharevision-logo.svg" alt="ShareVision Logo" className="h-20 mb-4" />
                    <p className="text-lg text-cyan-200 mb-4">SHAR Projects Pvt. Ltd</p>
                    <p className="text-xl text-cyan-100 font-medium">Camera Management System</p>
                </div>
            </div>

            {/* Right side - Reset password form */}
            <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100 py-8 px-4">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="flex flex-col items-center mb-8 lg:hidden text-center">
                        <img src="/assets/sharevision-logo.svg" alt="ShareVision Logo" className="h-14 mb-2" />
                        <p className="text-sm text-gray-600">SHAR Projects Pvt. Ltd</p>
                        <p className="text-sm text-gray-500">Camera Management System</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-8 border border-indigo-100">
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Reset Password</h2>
                            <p className="text-gray-600">Enter your new password</p>
                        </div>

                        {/* Error message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* Success message */}
                        {success && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm text-green-600">{success}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email address
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        className="input pl-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                        placeholder="Enter your email"
                                        {...register('email', {
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address'
                                            }
                                        })}
                                    />
                                </div>
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-1">
                                    Reset Token
                                </label>
                                <div className="relative">
                                    <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        id="token"
                                        type="text"
                                        className="input pl-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                        placeholder="Reset token"
                                        {...register('token', {
                                            required: 'Reset token is required'
                                        })}
                                    />
                                </div>
                                {errors.token && (
                                    <p className="mt-1 text-sm text-red-600">{errors.token.message}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                    New Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        id="newPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        className="input pl-10 pr-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                        placeholder="Enter new password"
                                        {...register('newPassword', {
                                            required: 'New password is required',
                                            minLength: {
                                                value: 6,
                                                message: 'Password must be at least 6 characters'
                                            },
                                            pattern: {
                                                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                                                message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
                                            }
                                        })}
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                {errors.newPassword && (
                                    <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
                                )}
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="btn w-full py-3 rounded-lg text-lg font-semibold shadow-md bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 to-indigo-800 text-white transition"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Resetting Password...
                                        </div>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </button>
                            </div>

                            <div className="text-center">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center text-sm text-cyan-600 hover:text-cyan-500 transition"
                                >
                                    <ArrowLeft className="h-4 w-4 mr-1" />
                                    Back to login
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ResetPassword 